// useAvailableDrivers.js
// Custom hook for fetching and managing available drivers

import { useState, useEffect, useCallback } from 'react'; // Added useCallback
import axios from 'axios';

/**
 * Custom hook for fetching and managing available drivers
 * @param {boolean} enabled Flag to enable/disable the hook
 * @param {string|null} jobZip Optional job zip code for distance filtering
 * @param {boolean} skipDistanceFilter Optional flag to skip distance filtering
 * @returns {Object} Available drivers data and related state
 */
const useAvailableDrivers = (enabled = true, jobZip = null, skipDistanceFilter = false) => { // Added skipDistanceFilter parameter
  // State for available drivers
  const [availableDrivers, setAvailableDrivers] = useState([]);

  // State for loading and error
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => { // Memoize getAuthHeader
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []); // Empty dependency array means it's created once

  // Fetch available drivers when enabled or jobZip changes
  const fetchAvailableDrivers = useCallback(async () => { // Memoize fetch function
    // console.log(`[useAvailableDrivers] fetchAvailableDrivers called. Enabled: ${enabled}, jobZip: ${jobZip}`); // Debug log
    if (!enabled) {
      // console.log('[useAvailableDrivers] Hook disabled, clearing drivers.'); // Debug log
      setAvailableDrivers([]); // Clear drivers if not enabled
      return;
    }

    // console.log('[useAvailableDrivers] Setting loading true.'); // Debug log
    setIsLoading(true);
    setError(null);

    try {
      // Construct query parameters
      const params = {};
      if (jobZip) {
        params.jobZip = jobZip; // Add jobZip if provided
      }
      
      // Add skipDistanceFilter parameter if true
      if (skipDistanceFilter) {
        params.skipDistanceFilter = 'true';
      }

      // console.log('[useAvailableDrivers] Fetching with params:', params); // Log params

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/available`, {
        headers: getAuthHeader(),
        params: params // Pass params to axios
      });

      // *** Log response data before setting state ***
      // console.log('[useAvailableDrivers] Received API response data:', response.data);
      // console.log('[useAvailableDrivers] Type of response data:', typeof response.data, 'Is Array:', Array.isArray(response.data));

      // *** Set State ***
      setAvailableDrivers(response.data || []); // Ensure it's always an array

    } catch (error) {
      console.error('Error fetching available drivers:', error);
      setError(error.message || 'Failed to load available drivers');
      console.log('[useAvailableDrivers] Error occurred, clearing drivers.'); // Debug log
      setAvailableDrivers([]); // Clear drivers on error
    } finally {
      // console.log('[useAvailableDrivers] Setting loading false.'); // Debug log
      setIsLoading(false);
    }
  }, [enabled, jobZip, skipDistanceFilter, getAuthHeader]); // Add skipDistanceFilter to dependencies

  // Effect to trigger fetch
  useEffect(() => {
    // console.log('[useAvailableDrivers] useEffect[fetchAvailableDrivers] triggered.'); // Debug log
    fetchAvailableDrivers();
  }, [fetchAvailableDrivers]); // Depend on the memoized fetch function

  // *** Log state whenever it changes ***
  useEffect(() => {
    // console.log('[useAvailableDrivers] State updated - availableDrivers:', availableDrivers);
  }, [availableDrivers]);


  return {
    availableDrivers,
    isLoading,
    error
  };
};

export default useAvailableDrivers;
